import React, { useState, useEffect } from 'react';
import { FaBan, FaCalendarAlt, FaChevronLeft, FaChevronRight, FaUsers, FaTrashAlt, FaLock } from 'react-icons/fa';
import { blockedSlotsService, facilitiesService } from '../../services/sportsManagementService';
import api from '../../services/api';
import { isAdminUser } from '../../services/authService';
import { Modal, message } from 'antd';
import './SportsManagementStyle.scss';
import PremiumAppointmentDetailView from '../SportsAppointments/PremiumAppointmentDetailView';

const getSlotOccupancyState = (slot, facility) => {
    let capacityValue = Number(slot?.capacity ?? slot?.Capacity ?? 1);
    const currentValue = Number(slot?.currentCount ?? slot?.CurrentCount ?? 0);

    if (facility) {
        const isPool = facility.facilityName?.toLowerCase().includes('havuz') || facility.name?.toLowerCase().includes('havuz') || facility.type?.toLowerCase().includes('havuz') || facility.typeName?.toLowerCase().includes('havuz');
        if (!isPool) {
            capacityValue = 1;
        }
    }

    return {
        capacity: Number.isFinite(capacityValue) && capacityValue > 0 ? capacityValue : 1,
        currentCount: Number.isFinite(currentValue) && currentValue > 0 ? currentValue : 0
    };
};

const BlockedSlotsManagement = ({ userRegions, showToast }) => {
    const isAdmin = isAdminUser();
    const [facilities, setFacilities] = useState([]);
    const [selectedFacility, setSelectedFacility] = useState('');
    const [selectedSportTypeId, setSelectedSportTypeId] = useState(null);
    const [loading, setLoading] = useState(false);

    const [currentWeekStart, setCurrentWeekStart] = useState(() => {
        const today = new Date();
        const day = today.getDay();
        const diff = today.getDate() - day + (day === 0 ? -6 : 1);
        const monday = new Date(today.setDate(diff));
        monday.setHours(0, 0, 0, 0);
        return monday;
    });

    const [weekSchedule, setWeekSchedule] = useState({});
    const [adminBlocks, setAdminBlocks] = useState([]);

    // Modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedCell, setSelectedCell] = useState(null);
    const [blockReason, setBlockReason] = useState('');
    const [isWeekly, setIsWeekly] = useState(false);
    const [blockSourceType, setBlockSourceType] = useState('Maintenance');
    
    // New states for read-only modal
    const [isReadOnlyModal, setIsReadOnlyModal] = useState(false);
    const [readOnlyBlockData, setReadOnlyBlockData] = useState(null);
    const [slotAppointments, setSlotAppointments] = useState([]);
    const [slotAppointmentsLoading, setSlotAppointmentsLoading] = useState(false);
    const [appointmentDetail, setAppointmentDetail] = useState(null);
    const [appointmentDetailLoading, setAppointmentDetailLoading] = useState(false);

    // ESC ile modal kapatma
    useEffect(() => {
        const handleEsc = (e) => { if (e.key === 'Escape') setIsModalOpen(false); };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, []);

    useEffect(() => {
        loadFacilities();
    }, []);

    useEffect(() => {
        if (selectedFacility) {
            fetchWeekData();
        } else {
            setWeekSchedule({});
            setAdminBlocks([]);
        }
    }, [selectedFacility, currentWeekStart, selectedSportTypeId]);

    const loadFacilities = async () => {
        try {
            const res = await facilitiesService.getAll(true);
            if (res?.isSuccess) {
                let fetchedFacilities = res.data;
                if (userRegions && userRegions.length > 0) {
                    const userRole = JSON.parse(localStorage.getItem('user') || '{}').userType?.toLowerCase();
                    if (userRole === 'regionalsports') {
                        const assignedIds = userRegions.map(ur => ur.id);
                        fetchedFacilities = fetchedFacilities.filter(f => assignedIds.includes(f.regionId));
                    }
                }
                setFacilities(fetchedFacilities);
            }
        } catch (error) {
            console.error('Tesisler yüklenemedi:', error);
        }
    };

    const formatDateString = (date) => {
        const d = new Date(date);
        d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
        return d.toISOString().split('T')[0];
    };

    const fetchWeekData = async () => {
        if (!selectedFacility) return;
        setLoading(true);
        try {
            const blocksRes = await blockedSlotsService.getByFacility(selectedFacility);
            if (blocksRes?.isSuccess) {
                setAdminBlocks(blocksRes.data);
            }

            const schedule = {};
            const dates = Array.from({ length: 7 }).map((_, i) => {
                const d = new Date(currentWeekStart);
                d.setDate(d.getDate() + i);
                return d;
            });

            await Promise.all(dates.map(async (dateObj) => {
                const dateStr = formatDateString(dateObj);
                try {
                    const res = await blockedSlotsService.getAvailableSlots(selectedFacility, dateStr, selectedSportTypeId);
                    if (res?.isSuccess) {
                        schedule[dateStr] = res.data;
                    } else {
                        schedule[dateStr] = [];
                    }
                } catch (e) {
                    schedule[dateStr] = [];
                }
            }));

            setWeekSchedule(schedule);
        } catch (error) {
            console.error('Haftalık veri yüklenemedi:', error);
        } finally {
            setLoading(false);
        }
    };

    const handlePrevWeek = () => {
        const prev = new Date(currentWeekStart);
        prev.setDate(prev.getDate() - 7);
        setCurrentWeekStart(prev);
    };

    const handleNextWeek = () => {
        const next = new Date(currentWeekStart);
        next.setDate(next.getDate() + 7);
        setCurrentWeekStart(next);
    };

    const handleCurrentWeek = () => {
        const today = new Date();
        const day = today.getDay();
        const diff = today.getDate() - day + (day === 0 ? -6 : 1);
        const monday = new Date(today.setDate(diff));
        monday.setHours(0, 0, 0, 0);
        setCurrentWeekStart(monday);
    };

    // Calculate columns
    const weekDates = Array.from({ length: 7 }).map((_, i) => {
        const d = new Date(currentWeekStart);
        d.setDate(d.getDate() + i);
        return d;
    });

    const dayNames = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];

    // Dynamic working hours from selected facility
    const selectedFacilityObj = facilities.find(f => f.facilityId === parseInt(selectedFacility));
    const workStartStr = selectedFacilityObj?.workingHoursStart || selectedFacilityObj?.WorkingHoursStart || '08:00';
    const workEndStr = selectedFacilityObj?.workingHoursEnd || selectedFacilityObj?.WorkingHoursEnd || '00:00';
    const duration = parseInt(selectedFacilityObj?.slotDurationInMinutes || selectedFacilityObj?.SlotDurationInMinutes || 60, 10);

    const [wsH, wsM] = workStartStr.split(':').map(Number);
    const [weH, weM] = workEndStr.split(':').map(Number);

    let startTotalMinutes = wsH * 60 + wsM;
    let endTotalMinutes = weH * 60 + weM;
    if (endTotalMinutes <= startTotalMinutes) endTotalMinutes += 24 * 60;

    const totalMinutes = endTotalMinutes - startTotalMinutes;
    const totalHours = Math.ceil(totalMinutes / 60);
    const pixelsPerMinute = 1.25; // 75px / 60min
    const gridHeight = totalMinutes * pixelsPerMinute;
    const facilityStartHour = wsH;

    // Collect unique hours is no longer needed for the grid, but we might keep it for reference or remove it
    // const allHours = Array.from(new Set(...))...

    const handleCellClick = async (dateStr, slot) => {
        if (!slot) return;

        const status = slot.status || slot.Status;
        const occupancy = getSlotOccupancyState(slot, selectedFacilityObj);
        const blockSource = slot.blockSource || slot.BlockSource;
        const isAvailableNormal = status === 'Available' && occupancy.capacity <= 1;

        const isClubSlot = !status.includes('Available') && blockSource === 'Club';
        const isBlocked = !status.includes('Available') && (blockSource === 'Maintenance' || blockSource === 'MemberHour' || blockSource === 'MinistryHour');
        const isCapacitySlot = occupancy.capacity > 1 && !isClubSlot && !isBlocked;

        const timeStr = slot.time || slot.Time;
        const timeParts = timeStr.split(' - ');
        const cellStartTime = timeParts[0].trim();
        let cellEndTime = timeParts[1].trim();
        if (cellEndTime === '00:00') cellEndTime = '24:00';

        const cellDateObj = new Date(dateStr);
        const cellDayOfWeek = cellDateObj.getDay();

        // 1. Durumlar: 
        // A) Tamamen Müsait Normal Slot (capacity=1, current=0) -> Sadece Kapatma Formu
        // B) Kapasite Slotu (Müsait veya Dolu) -> Liste + (Eğer tamamen dolu değilse Kapatma Formu)
        // C) Kapatılmış veya Dolu Normal Slot -> Sadece Detay (Silme seçeneği ile)

        if (status === 'Available' && !isCapacitySlot) {
            // A) Normal Müsait Slot
            setSelectedCell({ date: dateStr, time: timeStr, dayOfWeek: cellDayOfWeek });
            setBlockReason('');
            setIsWeekly(false);
            setBlockSourceType('Maintenance');
            setIsReadOnlyModal(false);
            setReadOnlyBlockData(null);
            setIsModalOpen(true);
        } else {
            // B veya C: Kapasite slotu VEYA Kapatılmış/Dolu normal slot

            const matchedBlocks = adminBlocks.filter(b => {
                let bStartTime = b.startTime || b.StartTime;
                let bEndTime = b.endTime || b.EndTime;
                if (bEndTime === '00:00') bEndTime = '24:00';

                const isTimeMatch = bStartTime < cellEndTime && bEndTime > cellStartTime;
                const isDateMatch = formatDateString(b.blockedDate || b.BlockedDate) === dateStr;
                const isWeeklyMatch = (b.isWeekly || b.IsWeekly) && (b.dayOfWeek === cellDayOfWeek || b.DayOfWeek === cellDayOfWeek);

                return isTimeMatch && (isDateMatch || isWeeklyMatch);
            });

            setSelectedCell({ date: dateStr, time: timeStr, dayOfWeek: cellDayOfWeek });
            
            setIsReadOnlyModal(true);
            
            setReadOnlyBlockData({
                source: isCapacitySlot ? 'Capacity' : (blockSource || 'Bilinmiyor'),
                reason: isCapacitySlot
                    ? `${occupancy.currentCount}/${occupancy.capacity} doluluk`
                    : (slot.blockReason || slot.BlockReason || 'Belirtilmemis'),
                occupancy: isCapacitySlot ? occupancy : null,
                matchedBlock: matchedBlocks[0] || null,
                matchedBlocks: matchedBlocks,
                canDelete: blockSource === 'Maintenance' || blockSource === 'MemberHour' || blockSource === 'MinistryHour' || blockSource === 'Club',
                isStillAvailable: status === 'Available'
            });
            
            setSlotAppointments([]);
            setAppointmentDetail(null);
            setBlockReason('');
            setIsWeekly(false);
            setBlockSourceType('Maintenance');
            setIsModalOpen(true);

            // Kapasite slotu ise randevu listesini getir
            if (isCapacitySlot && occupancy.currentCount > 0) {
                setSlotAppointmentsLoading(true);
                try {
                    const response = await api.get(
                        `/api/sports/slot-appointments/${selectedFacility}`,
                        { params: { date: dateStr, startTime: cellStartTime, endTime: cellEndTime } }
                    );
                    setSlotAppointments(response.data || response || []);
                } catch (err) {
                    console.error('Slot randevulari yuklenirken hata:', err);
                    setSlotAppointments([]);
                } finally {
                    setSlotAppointmentsLoading(false);
                }
            }

            // Normal dolu slot ise (bireysel randevu veya kulup) detay bilgisini getir
            if ((status === 'Full' || blockSource === 'Club') && !isCapacitySlot && (!blockSource || blockSource === 'Appointment' || blockSource === 'Club')) {
                setAppointmentDetailLoading(true);
                try {
                    const response = await api.get(
                        `/api/sports/appointment-details/${selectedFacility}`,
                        { params: { date: dateStr, startTime: cellStartTime } }
                    );
                    setAppointmentDetail(response.data || response || null);
                } catch (err) {
                    console.error('Randevu detayi yuklenirken hata:', err);
                    setAppointmentDetail(null);
                } finally {
                    setAppointmentDetailLoading(false);
                }
            }
        }
    };

    const handleBlockSubmit = async (e) => {
        e.preventDefault();
        if (!selectedCell) return;

        const timeParts = selectedCell.time.split(' - ');
        const startTime = timeParts[0].trim();
        const endTime = timeParts[1].trim();

        const payload = {
            facilityId: parseInt(selectedFacility),
            blockedDate: selectedCell.date,
            startTime: startTime,
            endTime: endTime,
            reason: blockReason || (blockSourceType === 'MemberHour' ? 'Üye Saati' : blockSourceType === 'MinistryHour' ? 'Bakanlık Saati' : 'Bakım'),
            blockSource: blockSourceType,
            createdBy: 'Admin',
            isWeekly: isWeekly,
            dayOfWeek: selectedCell.dayOfWeek
        };

        Modal.confirm({
            title: 'Onay',
            content: 'Bu saati takvimden kapatmak/bloke etmek istediğinize emin misiniz?',
            okText: 'Evet, Kapat',
            cancelText: 'Vazgeç',
            okType: 'danger',
            onOk: async () => {
                if (!isAdmin) {
                    if (showToast) showToast('Bu işlem için yöneticiniz ile görüşün.', 'error');
                    else message.error('Bu işlem için yöneticiniz ile görüşün.');
                    return;
                }
                try {
                    await blockedSlotsService.create(payload);
                    setIsModalOpen(false);
                    fetchWeekData();
                    message.success('Saat başarıyla engellendi.');
                } catch (error) {
                    console.error('Blokaj eklenirken hata:', error);
                    message.error('Blokaj eklenemedi. Lütfen tekrar deneyin.');
                }
            }
        });
    };

    const deleteBlock = async (id) => {
        Modal.confirm({
            title: 'Blokaj Kaldırma Onayı',
            content: 'Bu saati tekrar randevuya açmak istediğinize emin misiniz?',
            okText: 'Evet, Aç',
            cancelText: 'Hayır',
            onOk: async () => {
                if (!isAdmin) {
                    if (showToast) showToast('Bu işlem için yöneticiniz ile görüşün.', 'error');
                    else message.error('Bu işlem için yöneticiniz ile görüşün.');
                    return;
                }
                try {
                    await blockedSlotsService.delete(id);
                    fetchWeekData();
                    setIsModalOpen(false);
                    message.success('Blokaj kaldırıldı.');
                } catch (error) {
                    console.error('Blokaj kaldırılırken hata:', error);
                    message.error('Blokaj kaldırılamadı.');
                }
            }
        });
    };

    return (
        <div className="sports-management-page">
            <div className="page-header">
                <h1><FaCalendarAlt /> Haftalik Tesis Programi (Blokaj / Bakim)</h1>
            </div>

            <div className="premium-card" style={{ padding: '1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '2rem' }}>
                    <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
                        <div className="form-group" style={{ marginBottom: 0, minWidth: '300px' }}>
                            <select
                                value={selectedFacility}
                                onChange={(e) => {
                                    const facId = e.target.value;
                                    setSelectedFacility(facId);
                                    const fac = facilities.find(f => f.facilityId === parseInt(facId));
                                    if (fac?.isSharedFacility && fac?.availableSports?.length > 0) {
                                        setSelectedSportTypeId(fac.availableSports[0].typeId);
                                    } else {
                                        setSelectedSportTypeId(null);
                                    }
                                }}
                            >
                                <option value="">Islem Yapilacak Tesisi Seciniz</option>
                                {facilities.map(f => (
                                    <option key={f.facilityId} value={f.facilityId}>{f.facilityName} ({f.regionName})</option>
                                ))}
                            </select>
                        </div>
                        {(() => {
                            const fac = facilities.find(f => f.facilityId === parseInt(selectedFacility));
                            return fac?.isSharedFacility && fac?.availableSports?.length > 0 ? (
                                <div className="form-group" style={{ marginBottom: 0, minWidth: '180px' }}>
                                    <select
                                        value={selectedSportTypeId || ''}
                                        onChange={(e) => setSelectedSportTypeId(parseInt(e.target.value))}
                                        style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #d1d3e2', fontWeight: '600' }}
                                    >
                                        {fac.availableSports.map(sport => (
                                            <option key={sport.typeId} value={sport.typeId}>{sport.typeName}</option>
                                        ))}
                                    </select>
                                </div>
                            ) : null;
                        })()}
                    </div>

                    {selectedFacility && (
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                            <button className="btn-premium btn-outline" onClick={handlePrevWeek}><FaChevronLeft /> Önceki</button>
                            <button className="btn-premium btn-outline" onClick={handleCurrentWeek}>Mevcut Hafta</button>
                            <button className="btn-premium btn-outline" onClick={handleNextWeek}>Sonraki <FaChevronRight /></button>
                            <div style={{ fontWeight: 'bold', marginLeft: '1rem', color: '#2d3748' }}>
                                {formatDateString(weekDates[0])} - {formatDateString(weekDates[6])}
                            </div>
                        </div>
                    )}
                </div>

                {selectedFacility && (
                    <div className="calendar-container" style={{ overflowX: 'auto' }}>
                        {loading ? (
                            <div className="loading-spinner">Takvim Yükleniyor...</div>
                        ) : (
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: '80px repeat(7, 1fr)',
                                gap: '1px',
                                background: '#e2e8f0',
                                border: '1px solid #e2e8f0',
                                position: 'relative',
                                borderRadius: '12px',
                                overflow: 'hidden',
                                minWidth: '900px'
                            }}>
                                {/* Time Markers Column */}
                                <div style={{ background: '#f8fafc', display: 'flex', flexDirection: 'column', borderRight: '1px solid #cbd5e0' }}>
                                    <div style={{ padding: '12px', background: '#edf2f7', borderBottom: '2px solid #cbd5e0', height: '60px' }}></div>
                                    <div style={{ position: 'relative', height: `${gridHeight}px` }}>
                                        {Array.from({ length: totalHours }).map((_, i) => {
                                            const hourVal = (facilityStartHour + i) % 24;
                                            return (
                                                <div key={i} style={{
                                                    position: 'absolute',
                                                    top: `${i * 75}px`,
                                                    left: 0,
                                                    right: 0,
                                                    textAlign: 'center',
                                                    fontSize: '0.75rem',
                                                    color: '#718096',
                                                    fontWeight: 'bold',
                                                    paddingTop: '4px'
                                                }}>
                                                    {String(hourVal).padStart(2, '0')}:00
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                {weekDates.map((date, dayIdx) => {
                                    const dateStr = formatDateString(date);
                                    const daySlots = weekSchedule[dateStr] || [];
                                    const dayBaselineMinutes = startTotalMinutes;


                                    const getMinutes = (timeStr) => {
                                        if (!timeStr) return 0;
                                        if (timeStr === '24:00' || timeStr === '00:00') return 24 * 60;
                                        const [h, m] = timeStr.split(':').map(Number);
                                        return (h * 60) + m;
                                    };

                                    return (
                                        <div key={dayIdx} style={{ background: '#f8fafc', display: 'flex', flexDirection: 'column' }}>
                                            {/* Header */}
                                            <div style={{ padding: '12px', background: '#edf2f7', borderBottom: '2px solid #cbd5e0', textAlign: 'center', height: '60px', zIndex: 10 }}>
                                                <div style={{ fontWeight: 'bold', color: '#2d3748', fontSize: '0.9rem' }}>{dayNames[dayIdx]}</div>
                                                <div style={{ fontSize: '0.7rem', color: '#718096' }}>{date.toLocaleDateString('tr-TR')}</div>
                                            </div>

                                            {/* Body */}
                                            <div style={{ position: 'relative', height: `${gridHeight}px`, background: `repeating-linear-gradient(#fff, #fff 74px, #e2e8f0 74px, #e2e8f0 75px)` }}>
                                                {daySlots.map((slot, slotIdx) => {
                                                    const status = slot.status || slot.Status;
                                                    const timeStr = slot.time || slot.Time;
                                                    const [startT, endT] = timeStr.split(' - ').map(s => s.trim());

                                                    const startMinutes = getMinutes(startT);
                                                    const endMinutes = getMinutes(endT);
                                                    const duration = endMinutes - startMinutes;

                                                    const topPos = (startMinutes - dayBaselineMinutes) * pixelsPerMinute;
                                                    const heightPos = duration * pixelsPerMinute;

                                                    let bgColor = '#fff';
                                                    let textColor = '#2d3748';
                                                    let borderColor = '#e2e8f0';
                                                    let subContent = '';
                                                    const occupancy = getSlotOccupancyState(slot, selectedFacilityObj);
                                                    const showOccupancy = occupancy.capacity > 1;
                                                    const occupancyLabel = `${occupancy.currentCount}/${occupancy.capacity}`;

                                                    if (status === 'Available') {
                                                        const slotGender = slot.allowedGender || slot.AllowedGender;
                                                        if (slotGender === 'Male') {
                                                            bgColor = '#eff6ff';
                                                            borderColor = '#93c5fd';
                                                            textColor = '#1e40af';
                                                            subContent = '♂ Erkek Saati';
                                                        } else if (slotGender === 'Female') {
                                                            bgColor = '#fdf2f8';
                                                            borderColor = '#f9a8d4';
                                                            textColor = '#9d174d';
                                                            subContent = '♀ Kadın Saati';
                                                        } else {
                                                            bgColor = '#f0fff4';
                                                            borderColor = '#c6f6d5';
                                                            textColor = '#22543d';
                                                            subContent = 'Uygun';
                                                        }
                                                    } else if (status === 'Full') {
                                                        bgColor = '#fff5f5';
                                                        borderColor = '#fed7d7';
                                                        textColor = '#822727';
                                                        subContent = showOccupancy ? `${occupancyLabel} Dolu` : 'Dolu';
                                                    } else if (status === 'Blocked') {
                                                        const source = slot.blockSource || slot.BlockSource;
                                                        if (source === 'MemberHour') {
                                                            bgColor = '#fef3c7';
                                                            borderColor = '#f59e0b';
                                                            textColor = '#92400e';
                                                            subContent = slot.blockReason || 'Üye Saati';
                                                        } else if (source === 'MinistryHour') {
                                                            bgColor = '#d1fae5';
                                                            borderColor = '#059669';
                                                            textColor = '#064e3b';
                                                            subContent = slot.blockReason || 'Bakanlık Saati';
                                                        } else if (source === 'Maintenance') {
                                                            bgColor = '#fed7d7';
                                                            borderColor = '#f56565';
                                                            textColor = '#9b2c2c';
                                                            subContent = slot.blockReason || 'Bakım';
                                                        } else if (source === 'PublicEducationCourse') {
                                                            bgColor = '#e0e7ff';
                                                            borderColor = '#818cf8';
                                                            textColor = '#3730a3';
                                                            subContent = slot.blockReason || 'Halk Eğitim Dersi';
                                                        } else if (source === 'PublicEducation') {
                                                            bgColor = '#f3e8ff';
                                                            borderColor = '#a78bfa';
                                                            textColor = '#6b21a8';
                                                            subContent = slot.blockReason || 'Halk Eğitim Engeli';
                                                        } else {
                                                            bgColor = '#fffaf0';
                                                            borderColor = '#fbd38d';
                                                            textColor = '#744210';
                                                            subContent = slot.blockReason || 'Kısıtlı';
                                                        }
                                                    }

                                                    if (showOccupancy && status === 'Available') {
                                                        subContent = `${subContent} • ${occupancyLabel}`;
                                                    }

                                                    return (
                                                        <div
                                                            key={slotIdx}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleCellClick(dateStr, slot);
                                                            }}
                                                            style={{
                                                                position: 'absolute',
                                                                top: `${topPos}px`,
                                                                left: '4px',
                                                                right: '4px',
                                                                height: `${heightPos - 2}px`,
                                                                padding: '4px',
                                                                background: bgColor,
                                                                border: `1px solid ${borderColor}`,
                                                                borderRadius: '6px',
                                                                cursor: 'pointer',
                                                                transition: 'all 0.2s',
                                                                fontSize: '0.75rem',
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                textAlign: 'center',
                                                                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                                                zIndex: 1,
                                                                overflow: 'hidden'
                                                            }}
                                                            onMouseEnter={(e) => {
                                                                e.currentTarget.style.zIndex = '5';
                                                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                                                                e.currentTarget.style.transform = 'scale(1.02)';
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                e.currentTarget.style.zIndex = '1';
                                                                e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.05)';
                                                                e.currentTarget.style.transform = 'scale(1)';
                                                            }}
                                                        >
                                                            <div style={{ fontWeight: 'bold' }}>{timeStr}</div>
                                                            <div style={{ fontSize: '0.65rem', opacity: 0.8, color: textColor }}>{subContent}</div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Block Modal */}
            {isModalOpen && (
                <div className="modal-overlay" style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(15, 23, 42, 0.7)', zIndex: 1050,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: '24px',
                    backdropFilter: 'blur(8px)',
                }} onClick={() => setIsModalOpen(false)}>
                    <div className="modal-content" style={{
                        background: '#ffffff', borderRadius: '20px',
                        width: '100%', maxWidth: '540px',
                        maxHeight: '90vh', overflowY: 'auto',
                        boxShadow: '0 25px 50px -12px rgba(0,0,0,0.3)',
                        display: 'flex', flexDirection: 'column'
                    }} onClick={e => e.stopPropagation()}>
                        <div style={{ padding: '32px 32px 24px 32px' }}>
                        {isReadOnlyModal && readOnlyBlockData ? (
                            <div>
                                <h3 style={{ marginTop: 0, color: '#0f172a', borderBottom: '2px solid #f1f5f9', paddingBottom: '16px', marginBottom: '24px', display: 'flex', alignItems: 'center', fontSize: '1.35rem', fontWeight: 800 }}>
                                    <FaCalendarAlt style={{ color: '#0f75bc', marginRight: '12px', fontSize: '1.5rem' }} /> Randevu & Kapatma Detayı
                                </h3>
                                <div style={{ marginBottom: '1.5rem', background: '#f7fafc', padding: '1rem', borderRadius: '8px' }}>
                                    <p style={{ margin: '0 0 0.5rem', color: '#4a5568' }}><strong>Tarih:</strong> {new Date(selectedCell.date).toLocaleDateString('tr-TR')}</p>
                                    <p style={{ margin: 0, color: '#4a5568' }}><strong>Saat:</strong> {selectedCell.time}</p>
                                </div>
                                
                                <div style={{ marginBottom: '1.5rem', padding: '1rem', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#fff' }}>
                                    {appointmentDetailLoading ? (
                                        <p style={{ textAlign: 'center', color: '#9ca3af', padding: '12px 0', margin: 0 }}>Yukleniyor...</p>
                                    ) : appointmentDetail ? (
                                        <PremiumAppointmentDetailView appointmentDetail={appointmentDetail} />
                                    ) : readOnlyBlockData.source === 'Club' ? (
                                        <>
                                            <h4 style={{ margin: '0 0 1rem 0', color: '#2d3748', fontSize: '1rem' }}>
                                                Kulup Bilgileri {(readOnlyBlockData.matchedBlocks || []).length > 1 && <span style={{ fontWeight: 400, color: '#6b7280', fontSize: '0.85rem' }}>({(readOnlyBlockData.matchedBlocks || []).length} kulup)</span>}
                                            </h4>
                                            {(() => {
                                                const blocksInfo = [];
                                                (readOnlyBlockData.matchedBlocks || []).forEach(block => {
                                                    blocksInfo.push({
                                                        id: block.blockedSlotId || block.BlockedSlotId || Math.random(),
                                                        reason: block.reason || block.Reason || '',
                                                        isPaid: block.isPaid || block.IsPaid || false,
                                                        paidUntilDate: block.paidUntilDate || block.PaidUntilDate
                                                    });
                                                });
                                                if (blocksInfo.length === 0) return <p style={{ margin: 0, color: '#4a5568' }}>Bu saatte herhangi bir kulup atanmamis, ancak takvimden kapatilmis.</p>;
                                                return blocksInfo.map((block, index) => (
                                                    <div style={{ marginBottom: '16px' }} key={index}>
                                                        <PremiumAppointmentDetailView appointmentDetail={{
                                                            reservationType: 'Club',
                                                            firstName: block.reason,
                                                            lastName: '',
                                                            paymentStatus: block.isPaid ? 'Approved' : 'Pending',
                                                            status: block.isPaid ? 'Confirmed' : 'Pending',
                                                            blockSource: 'Club',
                                                            appointmentDate: selectedCell.date,
                                                            startTime: selectedCell.time.split(' - ')[0],
                                                            endTime: selectedCell.time.split(' - ')[1],
                                                            createdDate: new Date()
                                                        }} />
                                                    </div>
                                                ));
                                            })()}
                                        </>
                                    ) : (
                                        <>
                                            <h4 style={{ margin: '0 0 1rem 0', color: '#2d3748', fontSize: '1rem' }}>Durum Bilgisi</h4>
                                            <p style={{ margin: '0 0 0.5rem', color: '#4a5568' }}><strong>Kapatan Taraf / Tur:</strong> {readOnlyBlockData.source === 'Maintenance' ? 'Bakim' : readOnlyBlockData.source === 'MemberHour' ? 'Uye Saati' : readOnlyBlockData.source === 'MinistryHour' ? 'Bakanlik Saati' : readOnlyBlockData.source === 'Capacity' ? 'Kontenjan' : readOnlyBlockData.source}</p>
                                            <p style={{ margin: 0, color: '#4a5568' }}><strong>Detay (Sebep/Isim):</strong> {readOnlyBlockData.reason || 'Belirtilmemis'}</p>
                                            {readOnlyBlockData.occupancy && (
                                                <p style={{ margin: '0.5rem 0 0', color: '#be123c', fontWeight: 700 }}>
                                                    <strong>Doluluk:</strong> {readOnlyBlockData.occupancy.currentCount}/{readOnlyBlockData.occupancy.capacity}
                                                </p>
                                            )}
                                        </>
                                    )}
                                </div>

                                {/* Kapasite slotu ise randevu listesi */}
                                {readOnlyBlockData.source === 'Capacity' && (
                                    <div style={{ marginBottom: '1.5rem', padding: '1rem', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#fff' }}>
                                        <h4 style={{ margin: '0 0 0.75rem 0', color: '#2d3748', fontSize: '1rem' }}>
                                            Randevu Alan Kisiler {slotAppointments.length > 0 && <span style={{ fontWeight: 400, color: '#6b7280', fontSize: '0.85rem' }}>({slotAppointments.length})</span>}
                                        </h4>
                                        {slotAppointmentsLoading ? (
                                            <p style={{ textAlign: 'center', color: '#9ca3af', padding: '12px 0', margin: 0 }}>Yukleniyor...</p>
                                        ) : slotAppointments.length === 0 ? (
                                            <p style={{ textAlign: 'center', color: '#9ca3af', padding: '12px 0', margin: 0 }}>Randevu bulunamadi.</p>
                                        ) : (
                                            <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                                                    <thead>
                                                        <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                                                            <th style={{ textAlign: 'left', padding: '6px 8px', color: '#6b7280', fontWeight: 600 }}>#</th>
                                                            <th style={{ textAlign: 'left', padding: '6px 8px', color: '#6b7280', fontWeight: 600 }}>Isim Soyisim</th>
                                                            <th style={{ textAlign: 'center', padding: '6px 8px', color: '#6b7280', fontWeight: 600 }}>Odeme</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {slotAppointments.map((appt, idx) => {
                                                            const paid = (appt.paymentStatus || '').toLowerCase() === 'approved';
                                                            return (
                                                                <tr key={appt.id || idx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                                                    <td style={{ padding: '6px 8px', color: '#9ca3af' }}>{idx + 1}</td>
                                                                    <td style={{ padding: '6px 8px', color: '#111827', fontWeight: 500 }}>
                                                                        {appt.firstName} {appt.lastName}
                                                                    </td>
                                                                    <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                                                                        <span style={{
                                                                            display: 'inline-block',
                                                                            padding: '2px 8px',
                                                                            borderRadius: '10px',
                                                                            fontSize: '0.72rem',
                                                                            fontWeight: 600,
                                                                            background: paid ? '#d1fae5' : '#fee2e2',
                                                                            color: paid ? '#065f46' : '#991b1b'
                                                                        }}>
                                                                            {paid ? 'Odendi' : 'Odenmedi'}
                                                                        </span>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Listeyi gösterdikten sonra, eğer hala Available ise formu da gösterelim */}
                                {readOnlyBlockData.source === 'Capacity' && readOnlyBlockData.isStillAvailable && (
                                    <div style={{ marginTop: '2rem', borderTop: '2px dashed #cbd5e0', paddingTop: '1.5rem' }}>
                                        <h4 style={{ margin: '0 0 1rem 0', color: '#e53e3e', fontSize: '1rem', display: 'flex', alignItems: 'center' }}>
                                            <FaBan style={{ marginRight: '8px' }} /> Kalan Kontenjanı Kapat
                                        </h4>
                                        <div style={{ marginBottom: '1.5rem' }}>
                                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Kapatma Tipi</label>
                                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                                {[
                                                    { value: 'Maintenance', label: 'Bakım', color: '#e53e3e', bgColor: '#fff5f5', borderColor: '#fed7d7' },
                                                    { value: 'MemberHour', label: 'Üye Saati', color: '#92400e', bgColor: '#fef3c7', borderColor: '#f59e0b' },
                                                    { value: 'MinistryHour', label: 'Bakanlık Saati', color: '#064e3b', bgColor: '#d1fae5', borderColor: '#059669' }
                                                ].map(opt => {
                                                    const isSelected = blockSourceType === opt.value;
                                                    return (
                                                        <button
                                                            key={opt.value}
                                                            type="button"
                                                            onClick={() => {
                                                                setBlockSourceType(opt.value);
                                                                if (opt.value === 'MemberHour') setBlockReason('Üye Saati');
                                                                else if (opt.value === 'MinistryHour') setBlockReason('Bakanlık Saati');
                                                                else setBlockReason('');
                                                            }}
                                                            style={{
                                                                padding: '8px 16px',
                                                                borderRadius: '8px',
                                                                border: isSelected ? `2px solid ${opt.borderColor}` : '1.5px solid #d1d5db',
                                                                background: isSelected ? opt.bgColor : '#fff',
                                                                color: isSelected ? opt.color : '#6b7280',
                                                                fontWeight: '700',
                                                                fontSize: '0.85rem',
                                                                cursor: 'pointer',
                                                                flex: '1',
                                                                textAlign: 'center'
                                                            }}
                                                        >
                                                            {opt.label}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                        <form id="capacityBlockForm" onSubmit={handleBlockSubmit}>
                                            <div className="form-group" style={{ marginBottom: '1rem' }}>
                                                <input
                                                    type="text"
                                                    value={blockReason}
                                                    onChange={(e) => setBlockReason(e.target.value)}
                                                    placeholder="Sebep (Opsiyonel)"
                                                    style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e0', boxSizing: 'border-box' }}
                                                />
                                            </div>
                                            <div className="form-group" style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <input
                                                    type="checkbox"
                                                    id="isWeeklyToggleCapacity"
                                                    checked={isWeekly}
                                                    onChange={(e) => setIsWeekly(e.target.checked)}
                                                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                                                />
                                                <label htmlFor="isWeeklyToggleCapacity" style={{ cursor: 'pointer', fontSize: '0.85rem', color: '#2d3748', margin: 0 }}>
                                                    Her hafta tekrarla
                                                </label>
                                            </div>
                                        </form>
                                    </div>
                                )}

                                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '1.5rem', flexWrap: 'wrap' }}>
                                    <button type="button" onClick={() => setIsModalOpen(false)} style={{
                                        padding: '10px 16px', borderRadius: '6px', border: '1px solid #cbd5e0',
                                        background: '#fff', cursor: 'pointer', fontWeight: 'bold'
                                    }}>
                                        Kapat
                                    </button>
                                    
                                    {readOnlyBlockData.source !== 'Club' && readOnlyBlockData.canDelete && readOnlyBlockData.matchedBlock && (
                                        <button 
                                            type="button" 
                                            onClick={() => deleteBlock(readOnlyBlockData.matchedBlock.blockedSlotId)}
                                            style={{
                                                padding: '10px 16px', borderRadius: '6px', border: 'none',
                                                background: '#e53e3e', color: '#fff', cursor: 'pointer', fontWeight: 'bold'
                                            }}
                                        >
                                            Saati Ac (Sil)
                                        </button>
                                    )}

                                    {readOnlyBlockData.source === 'Capacity' && readOnlyBlockData.isStillAvailable && (
                                        <button type="submit" form="capacityBlockForm" style={{
                                            padding: '10px 16px', borderRadius: '6px', border: 'none',
                                            background: '#e53e3e', color: '#fff', cursor: 'pointer', fontWeight: 'bold',
                                        }}>
                                            Bu Saati Engelle
                                        </button>
                                    )}
                                </div>
                            </div>
                        ) : (
                            // Existing Create Block Form
                            <>
                                <h3 style={{ marginTop: 0, color: '#0f172a', borderBottom: '2px solid #f1f5f9', paddingBottom: '16px', marginBottom: '24px', display: 'flex', alignItems: 'center', fontSize: '1.35rem', fontWeight: 800 }}>
                                    <FaBan style={{ color: '#ef4444', marginRight: '12px', fontSize: '1.5rem' }} /> Seçili Saati Kapat
                                </h3>

                                <div style={{ marginBottom: '24px', background: '#f8fafc', padding: '16px 20px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                        <span style={{ color: '#64748b', fontWeight: 600 }}>Tarih:</span>
                                        <span style={{ color: '#1e293b', fontWeight: 700 }}>{new Date(selectedCell.date).toLocaleDateString('tr-TR')}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span style={{ color: '#64748b', fontWeight: 600 }}>Saat:</span>
                                        <span style={{ color: '#1e293b', fontWeight: 700 }}>{selectedCell.time}</span>
                                    </div>
                                </div>

                                {/* Block Type Radio Cards */}
                                <div style={{ marginBottom: '1.5rem' }}>
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Kapatma Tipi</label>
                                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                        {[
                                            { value: 'Maintenance', label: 'Bakım', color: '#e53e3e', bgColor: '#fff5f5', borderColor: '#fed7d7' },
                                            { value: 'MemberHour', label: 'Üye Saati', color: '#92400e', bgColor: '#fef3c7', borderColor: '#f59e0b' },
                                            { value: 'MinistryHour', label: 'Bakanlık Saati', color: '#064e3b', bgColor: '#d1fae5', borderColor: '#059669' }
                                        ].map(opt => {
                                            const isSelected = blockSourceType === opt.value;
                                            return (
                                                <button
                                                    key={opt.value}
                                                    type="button"
                                                    onClick={() => {
                                                        setBlockSourceType(opt.value);
                                                        if (opt.value === 'MemberHour') setBlockReason('Üye Saati');
                                                        else if (opt.value === 'MinistryHour') setBlockReason('Bakanlık Saati');
                                                        else setBlockReason('');
                                                    }}
                                                    style={{
                                                        padding: '12px 20px',
                                                        borderRadius: '10px',
                                                        border: isSelected ? `2.5px solid ${opt.borderColor}` : '1.5px solid #d1d5db',
                                                        background: isSelected ? opt.bgColor : '#fff',
                                                        color: isSelected ? opt.color : '#6b7280',
                                                        fontWeight: '700',
                                                        fontSize: '0.9rem',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s',
                                                        flex: '1',
                                                        minWidth: '100px',
                                                        textAlign: 'center',
                                                        boxShadow: isSelected ? `0 2px 8px ${opt.borderColor}40` : 'none'
                                                    }}
                                                >
                                                    {opt.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                <form onSubmit={handleBlockSubmit}>
                                    <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '700', color: '#1e293b' }}>Sebep (Opsiyonel)</label>
                                        <input
                                            type="text"
                                            value={blockReason}
                                            onChange={(e) => setBlockReason(e.target.value)}
                                            placeholder="Örn: Saha Bakımı, Turnuva..."
                                            style={{ width: '100%', padding: '14px', borderRadius: '10px', border: '2px solid #e2e8f0', boxSizing: 'border-box', outline: 'none', transition: 'border-color 0.2s', fontSize: '1rem' }}
                                            onFocus={(e) => e.target.style.borderColor = '#0f75bc'}
                                            onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                                            autoFocus
                                        />
                                    </div>

                                    <div className="form-group" style={{ marginBottom: '32px', display: 'flex', alignItems: 'center', gap: '12px', background: '#f0fdf4', padding: '16px', borderRadius: '10px', border: '1px solid #bcf0da' }}>
                                        <input
                                            type="checkbox"
                                            id="isWeeklyToggle"
                                            checked={isWeekly}
                                            onChange={(e) => setIsWeekly(e.target.checked)}
                                            style={{ width: '20px', height: '20px', cursor: 'pointer', accentColor: '#16a34a' }}
                                        />
                                        <label htmlFor="isWeeklyToggle" style={{ cursor: 'pointer', fontWeight: '700', color: '#166534', margin: 0, fontSize: '0.95rem' }}>
                                            Her hafta bu gün ve saatte tekrarlansın
                                        </label>
                                    </div>

                                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', paddingTop: '16px', borderTop: '2px solid #f1f5f9' }}>
                                        <button type="button" onClick={() => setIsModalOpen(false)} style={{
                                            padding: '12px 24px', borderRadius: '10px', border: '2px solid #e2e8f0',
                                            background: '#ffffff', color: '#475569', cursor: 'pointer', fontWeight: '700', fontSize: '1rem', transition: 'all 0.2s'
                                        }} onMouseEnter={(e) => e.target.style.background = '#f8fafc'} onMouseLeave={(e) => e.target.style.background = '#ffffff'}>
                                            Vazgeç
                                        </button>
                                        <button type="submit" style={{
                                            padding: '12px 24px', borderRadius: '10px', border: 'none',
                                            background: '#ef4444', color: '#fff', cursor: 'pointer', fontWeight: '700', fontSize: '1rem',
                                            boxShadow: '0 4px 14px 0 rgba(239, 68, 68, 0.39)', transition: 'all 0.2s'
                                        }} onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'} onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}>
                                            Kapatmayı Onayla
                                        </button>
                                    </div>
                                </form>
                            </>
                        )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BlockedSlotsManagement;
